/**
 * Tracks user interaction and playback activity for pausing background enrichment.
 */
import { useMaestroStore } from '../store/maestroStore';

/** Minutes without interaction AND without playback before enrichment pauses. */
export const ENRICHMENT_IDLE_PAUSE_MINUTES = 5;

const IDLE_MS = ENRICHMENT_IDLE_PAUSE_MINUTES * 60 * 1000;

let lastInteractionAt = Date.now();
let lastPlaybackAt = 0;
let listenersInstalled = false;
const subscribers = new Set<(idle: boolean) => void>();

function notify(): void {
  const idle = isAppIdle();
  for (const fn of subscribers) {
    try { fn(idle); } catch { /* ignore */ }
  }
}

export function markUserInteraction(): void {
  lastInteractionAt = Date.now();
  notify();
}

export function markPlaybackActivity(): void {
  lastPlaybackAt = Date.now();
  notify();
}

export function isAppIdle(nowMs = Date.now()): boolean {
  const noInteraction = nowMs - lastInteractionAt >= IDLE_MS;
  const noPlayback = lastPlaybackAt <= 0 || nowMs - lastPlaybackAt >= IDLE_MS;
  return noInteraction && noPlayback;
}

export function subscribeAppIdle(listener: (idle: boolean) => void): () => void {
  subscribers.add(listener);
  try { listener(isAppIdle()); } catch { /* ignore */ }
  return () => subscribers.delete(listener);
}

export function installAppIdleMonitor(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;

  const onActivity = () => markUserInteraction();
  for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const) {
    window.addEventListener(type, onActivity, { passive: true, capture: true });
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') markUserInteraction();
  });

  useMaestroStore.subscribe((state) => {
    if (state.playbackState === 'playing' || state.playbackState === 'loading') {
      markPlaybackActivity();
    }
  });
}
